/**
 * Checking things off on a phone (SPEC §8.1), so scrolling doesn't do it by accident:
 *  - a touch that lands while the list is still sliding (the thumb that stops a flick) isn't a
 *    tap on a box;
 *  - and, with Settings → Just for you → "Tap a task first to check it off" (on by default), a
 *    box in a list takes one tap to make its task ready (it looks selected, as the row being
 *    typed in does) and a second to check it. The row being typed in is ready already.
 * Mouse clicks and keys check straight away.
 */
import { useSyncExternalStore } from 'react'
import { store } from '../store/store'
import { myId } from '../store/me'
import { showToast } from '../store/toasts'
import { haptic } from './haptics'

let lastScroll = 0
if (typeof window !== 'undefined') window.addEventListener('scroll', () => (lastScroll = performance.now()), { capture: true, passive: true })

/** The list moved a moment ago (a flick still sliding). */
export const listMoving = () => performance.now() - lastScroll < 120
/** The list moved since `t` (the touch was a scroll, not a tap). */
export const movedSince = (t: number) => lastScroll > t

export const tapFirst = () => store.userSettings(myId()).tapFirstToCheck !== false

let armed: string | null = null
const subs = new Set<() => void>()
const emit = () => subs.forEach((f) => f())

// A tap anywhere else, or scrolling, and it's no longer ready.
const outside = (e: Event) => {
  const box = (e.target as Element | null)?.closest?.('[data-arm]')
  if (box?.getAttribute('data-arm') !== armed) disarm()
}
const onScroll = () => disarm()

/** The first tap on a list's box: its task is ready to check. */
export function arm(key: string) {
  armed = key
  emit()
  haptic(6)
  document.addEventListener('pointerdown', outside, true)
  window.addEventListener('scroll', onScroll, { capture: true, passive: true })
  // The first few times, what the second tap does.
  try {
    const n = Number(localStorage.getItem('tz.tapFirstHints') ?? 0)
    if (n < 3) {
      localStorage.setItem('tz.tapFirstHints', String(n + 1))
      showToast('Tap the box again to check it off', { timeout: 2500 })
    }
  } catch {
    /* no storage: no hint */
  }
}

export function disarm() {
  if (!armed) return
  armed = null
  emit()
  document.removeEventListener('pointerdown', outside, true)
  window.removeEventListener('scroll', onScroll, true)
}

export function useArmed(key: string | undefined): boolean {
  return useSyncExternalStore(
    (cb) => {
      subs.add(cb)
      return () => subs.delete(cb)
    },
    () => !!key && armed === key,
  )
}
